import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Building2, MapPin, Loader2 } from 'lucide-react';
import { useGoogleMapsReady } from '@/hooks/useGoogleMapsReady';

const METERS_PER_MILE = 1609.34;
const DEFAULT_MIN_RADIUS_MILES = 1;
const DEFAULT_MAX_RADIUS_MILES = 150;

export type RadiusMapHandle = {
  endUserEdit: () => void;
};

export type RadiusMapVisualizationProps = {
  radius: number;
  startLocation?: 'home' | 'office';
  coordinates?: { lat: number; lng: number } | null;
  minRadiusMiles?: number;
  maxRadiusMiles?: number;
  onRadiusChange?: (miles: number) => void;
  className?: string;
};

function getZoomForRadius(radiusMiles: number): number {
  if (radiusMiles <= 15) return 10;
  if (radiusMiles <= 25) return 9;
  if (radiusMiles <= 50) return 8;
  if (radiusMiles <= 75) return 7;
  return 6;
}

function centersMatch(
  a: { lat: number; lng: number },
  b: google.maps.LatLng | null | undefined,
): boolean {
  if (!b) return false;
  return Math.abs(a.lat - b.lat()) < 1e-7 && Math.abs(a.lng - b.lng()) < 1e-7;
}

export const RadiusMapVisualization = forwardRef<RadiusMapHandle, RadiusMapVisualizationProps>(({
  radius,
  startLocation = 'office',
  coordinates,
  minRadiusMiles = DEFAULT_MIN_RADIUS_MILES,
  maxRadiusMiles = DEFAULT_MAX_RADIUS_MILES,
  onRadiusChange,
  className,
}, ref) => {
  const { isReady: mapsReady, error: mapsError } = useGoogleMapsReady();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const isSyncingRef = useRef(false);
  const isUserEditingRef = useRef(false);
  const editIdleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastEmittedRadiusRef = useRef(radius);
  const onRadiusChangeRef = useRef(onRadiusChange);

  useEffect(() => {
    onRadiusChangeRef.current = onRadiusChange;
  }, [onRadiusChange]);

  useEffect(() => {
    lastEmittedRadiusRef.current = radius;
  }, [radius]);

  useImperativeHandle(ref, () => ({
    endUserEdit: () => {
      clearTimeout(editIdleTimerRef.current);
      isUserEditingRef.current = false;
    },
  }), []);

  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const containerClassName = className ?? 'h-72 sm:h-96';

  const setCircleCenterIfNeeded = (target: { lat: number; lng: number }) => {
    const circle = circleRef.current;
    if (!circle || centersMatch(target, circle.getCenter())) return;
    isSyncingRef.current = true;
    circle.setCenter(target);
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  };

  useEffect(() => {
    if (!mapsReady || !mapRef.current || !coordinates || mapInstanceRef.current) {
      return;
    }

    const initialRadius = lastEmittedRadiusRef.current;

    const map = new google.maps.Map(mapRef.current, {
      center: coordinates,
      zoom: getZoomForRadius(initialRadius),
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });
    mapInstanceRef.current = map;

    const circle = new google.maps.Circle({
      map,
      center: coordinates,
      radius: initialRadius * METERS_PER_MILE,
      editable: true,
      draggable: false,
      fillColor: '#2563eb',
      fillOpacity: 0.2,
      strokeColor: '#2563eb',
      strokeOpacity: 0.9,
      strokeWeight: 2,
    });
    circleRef.current = circle;

    markerRef.current = new google.maps.Marker({
      map,
      position: coordinates,
      clickable: false,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: startLocation === 'home' ? '#10B981' : '#3B82F6',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
    });

    listenersRef.current.push(
      circle.addListener('radius_changed', () => {
        if (isSyncingRef.current) return;
        const miles = Math.round(circle.getRadius() / METERS_PER_MILE);
        const clamped = Math.min(Math.max(miles, minRadiusMiles), maxRadiusMiles);
        if (clamped === lastEmittedRadiusRef.current) return;
        lastEmittedRadiusRef.current = clamped;
        isUserEditingRef.current = true;
        clearTimeout(editIdleTimerRef.current);
        editIdleTimerRef.current = setTimeout(() => {
          isUserEditingRef.current = false;
        }, 200);
        onRadiusChangeRef.current?.(clamped);
      })
    );

    const bounds = circle.getBounds();
    if (bounds) map.fitBounds(bounds);
    lastEmittedRadiusRef.current = initialRadius;
    setIsMapLoaded(true);
  }, [coordinates, startLocation, minRadiusMiles, maxRadiusMiles, mapsReady]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const circle = circleRef.current;
    const marker = markerRef.current;
    if (!map || !circle || !marker || !coordinates) return;

    setCircleCenterIfNeeded(coordinates);
    marker.setPosition(coordinates);
    map.panTo(coordinates);
  }, [coordinates]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const circle = circleRef.current;
    if (!map || !circle || !coordinates || isUserEditingRef.current) return;

    const currentMiles = Math.round(circle.getRadius() / METERS_PER_MILE);
    if (currentMiles === radius) return;

    isSyncingRef.current = true;
    lastEmittedRadiusRef.current = radius;
    circle.setRadius(radius * METERS_PER_MILE);
    if (!centersMatch(coordinates, circle.getCenter())) {
      circle.setCenter(coordinates);
    }
    const bounds = circle.getBounds();
    if (bounds) map.fitBounds(bounds);
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  }, [radius, coordinates]);

  useEffect(() => {
    return () => {
      clearTimeout(editIdleTimerRef.current);
      listenersRef.current.forEach((l) => l.remove());
      listenersRef.current = [];
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      mapInstanceRef.current = null;
      setIsMapLoaded(false);
    };
  }, []);

  if (!coordinates) {
    return (
      <div className={`relative rounded-xl border border-ink-200 bg-ink-50 flex items-center justify-center ${containerClassName}`}>
        <p className="text-sm text-ink-500 px-4 text-center">Add a business location to preview your service radius.</p>
      </div>
    );
  }

  if (mapsError) {
    return (
      <div className={`relative rounded-xl border border-red-200 bg-red-50 flex items-center justify-center ${containerClassName}`}>
        <p className="text-sm text-red-700 px-4 text-center">{mapsError}</p>
      </div>
    );
  }

  if (!mapsReady) {
    return (
      <div className={`relative rounded-xl border border-ink-200 bg-ink-50 flex items-center justify-center ${containerClassName}`}>
        <div className="flex flex-col items-center gap-2 text-ink-500">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <p className="text-sm">Loading map…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative rounded-xl overflow-hidden border border-ink-200 ${containerClassName}`}>
      <div ref={mapRef} className="w-full h-full min-h-[240px]" />

      <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-surface/95 rounded-lg shadow-md border border-ink-100">
        <span className="text-sm font-bold text-accent-700">{radius} mile radius</span>
      </div>

      <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 bg-surface/95 rounded-lg shadow-sm border border-ink-100">
        <Building2 className="w-3.5 h-3.5 text-info-600" />
        <span className="text-2xs font-medium text-ink-700">
          Business Location
        </span>
      </div>

      {isMapLoaded && (
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 bg-accent-600/95 rounded-lg shadow-md">
          <MapPin className="w-3 h-3 text-white" />
          <span className="text-2xs font-medium text-white">
            Drag the circle edge to adjust
          </span>
        </div>
      )}
    </div>
  );
});

RadiusMapVisualization.displayName = 'RadiusMapVisualization';
